import { FORECAST, PROTECTED } from '@/lib/constants/routes';
import {
  FORECAST_LAYOUT_TABS,
  type ForecastLayoutSearchSchema,
} from './forecastLayoutSearchSchema';

export const FORECAST_BASE_PATH = `${PROTECTED}${FORECAST}`;

type ForecastLayoutTab =
  (typeof FORECAST_LAYOUT_TABS)[keyof typeof FORECAST_LAYOUT_TABS];

/** Tab di default per le rotte figlie: un deep link apre il pannello sul suo tab. */
const CHILD_SEGMENT_TABS: Record<string, ForecastLayoutTab> = {
  planning: FORECAST_LAYOUT_TABS.PLANNING,
  recurrences: FORECAST_LAYOUT_TABS.RECURRENCES,
};

interface ResolveForecastLayoutRedirectParams {
  pathname: string;
  search: ForecastLayoutSearchSchema;
  currentYear: number;
}

export interface ForecastLayoutRedirect {
  to: string;
  search: ForecastLayoutSearchSchema;
}

const stripTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

/** Primo segmento dopo `/protected/forecast` (es. `planning`), se c'è. */
const childSegment = (pathname: string): string | null => {
  const normalized = stripTrailingSlash(pathname);
  if (!normalized.startsWith(`${FORECAST_BASE_PATH}/`)) return null;
  const [segment] = normalized.slice(FORECAST_BASE_PATH.length + 1).split('/');
  return segment || null;
};

/**
 * Completa i parametri obbligatori del previsionale (tab, anno, fonti).
 * `null` se non manca nulla. Una rotta figlia (es. `/planning/$id`) resta
 * dov'è e prende il tab del suo segmento: prima un link senza `year`
 * rimandava alla pagina base e il pannello non si apriva.
 */
export function resolveForecastLayoutRedirect({
  pathname,
  search,
  currentYear,
}: ResolveForecastLayoutRedirectParams): ForecastLayoutRedirect | null {
  const needsYear = search.year === undefined || search.year === null;
  if (!needsYear && search.tab) return null;

  const segment = childSegment(pathname);
  const defaultTab =
    (segment && CHILD_SEGMENT_TABS[segment]) ?? FORECAST_LAYOUT_TABS.FORECAST;

  return {
    to: segment ? stripTrailingSlash(pathname) : FORECAST_BASE_PATH,
    search: {
      ...search,
      tab: search.tab || defaultTab,
      year: search.year ?? currentYear,
      includeRealized: search.includeRealized ?? true,
      includeUnpaid: search.includeUnpaid ?? true,
      includeForecast: search.includeForecast ?? true,
      includeAvailableBalance: search.includeAvailableBalance ?? true,
    },
  };
}
